/**
 * ⑭ R14a — 탐색 v2 (NAV14_20260929 「R14a 탐색」). 옛 Navigation.tsx 는 킬 스위치(app_config ui/nav_v2=false)용으로 «그대로» 보존.
 *
 * ── 무엇이 달라졌나 ──
 *   · 서버에서 렌더된다(옛 것은 dynamic ssr:false 라 서버 HTML 에 메뉴 링크가 0개였다).
 *     드롭다운 2층 링크도 서버 HTML 에 전부 있다 — JS 없이도 링크가 존재한다.
 *   · 탭 5(RD: 홈·부동산·블로그·게시판·주식) = 모바일 하단 탭과 데스크톱 상단(홈 제외 4)이 같은 이름·같은 순서(nav-config 한 벌).
 *   · 드롭다운 = CSS :hover · :focus-within · 클릭(<details> 토글 + :has) — 스크립트 불필요.
 *     NavV2Effects 섬은 «보강» 만 한다(바깥 클릭·Esc 닫기 · 이동 뒤 닫기 · 계측).
 *
 * ⛔ 이 파일은 훅이 없다. 활성 탭은 pathname prop 으로만 정한다(방문자 무관 — 같은 URL = 같은 HTML).
 *    호출부(ClientShell)가 usePathname() 을 넘긴다. 서버 렌더에서 홈은 «/index» 로 온다 — activeTabOf 가 isHomePath 로 흡수.
 * ⛔ 로그인·봇·UA 분기 없음. 사용자별 부분은 NavUserMenu 섬뿐(SSR 에서는 같은 크기 빈 자리).
 * ⛔ sticky 바에 backdrop-filter 금지(DS_RULES §1-6). 그라디언트·이모지 없음.
 */
import Link from 'next/link';
import React, { type ReactNode } from 'react';
import { NAV_LABELS_V2_DEFAULT, NAV_TABS_V3_DEFAULT, activeTabOf, activeTopOf, navTabsOf, topMenuOf, type NavTabKey } from '@/lib/nav/nav-config';
import { navEvAttrs } from '@/lib/nav/nav-track';
import { NavSearchSlot } from './nav-slots';
import NavUserMenu from './NavUserMenu';
import NavVisitorCount from './NavVisitorCount';
import RegionMemorySync from './RegionMemorySync';
import NavV2Effects from './NavV2Effects';
import NavMoreMenu from './NavMoreMenu';

// DS2(ds2/sian/sian_chrome) — 헤더 56 · 로고 19/700 navy · 메뉴 「분양·청약 일정·재개발·재건축·지역·분석 글」 · 우측 지역 pill 1 + 검색 pill(모바일 아이콘).
//   방문자 카운터(LiveActivityIndicator) 렌더 없음(E2 · 파일 보존). 「도구」 는 상단에서 빼고 푸터·더보기에 둔다(시안 메뉴 5).
//   하단 탭 = 홈 · 분양 · 청약 · 재개발 · 블로그 · 더보기 — 58px + safe-area · 아이콘 22 선 1.7(활성 2.0) · 라벨 12. 링크는 전부 정적 <a href>.
//   nav-live(2026-10-24 Node) — 하단 탭에 「블로그」(/blog · /blog/** 활성) 추가 → 6칸 균등(360px 에서 칸 60px · 라벨 최장 3자 36px).
//     데스크톱 상단은 그대로 「분석 글」(같은 /blog). 더보기(/more)에는 /blog 항목이 없다(시리즈 /blog/series 만) — 중복 0.
//   nav-live — 우측 지역 칩 자리 → 「LIVE 최근 30일 조회 N회」 카운터(NavVisitorCount · 봇성 제외 실측). 칩은 하루 사용 ~3명(14일 38명)·첫 방문 제안 수락 0.25%(5/1981)였고,
//     지역 고르기는 지역 허브 셀렉 바·홈 히어로 시·도 칩·쪽 머리 칩·더보기 「지역 설정」에 그대로 있다.
//     칩이 하던 «허브 진입 시 지역 기억 동기화» 는 RegionMemorySync(화면 없음)로 옮겼다.
// L6 tabs-r1 — 탭 정의의 단일 출처는 nav-config NAV_TABS(라벨 한 벌 navLabel · 플래그 nav/labels_v2). 여기는 아이콘만 붙인다.
//   라벨(v2): 홈 · 분양 · 청약 일정 · 재개발 · 분석 글 · 더보기 — 데스크톱 상단과 같은 낱말. 종전(v1) 「청약」·「블로그」는 플래그 OFF 때.
const P = (d: string) => <path d={d} />;
const TAB_ICONS: Record<NavTabKey, ReactNode> = {
  home: P('M4 10.5 12 4l8 6.5V20h-5.5v-5.5h-5V20H4z'),
  apt: <><rect x="5" y="3.5" width="14" height="17" rx="1.5" />{P('M9 8h2M13 8h2M9 12h2M13 12h2M10.5 20.5v-3.5h3v3.5')}</>,
  calendar: <><rect x="4" y="5.5" width="16" height="15" rx="2" />{P('M4 10h16M8.5 3.5v4M15.5 3.5v4M9 14.5l2 2 4-4')}</>,
  redev: P('M3.5 20.5h17M6 20.5V11l4-3v12.5M10 20.5V6l8 4v10.5M13 12h2M13 15.5h2'),
  // nav-live — 블로그: 펼친 책(선 아이콘 · 같은 굵기·끝 모양).
  blog: P('M12 6.5C9.6 5 7 4.6 4 5.3v13.4c3-.7 5.6-.3 8 1.3 2.4-1.6 5-2 8-1.3V5.3c-3-.7-5.6-.3-8 1.2zM12 6.5V20'),
  // M6 — 지역(v3 탭): 지도 핀(선 아이콘 · 같은 굵기).
  region: <>{P('M12 21s-6.5-5.8-6.5-11a6.5 6.5 0 0 1 13 0c0 5.2-6.5 11-6.5 11z')}<circle cx="12" cy="10" r="2.3" /></>,
  more: <><circle cx="6" cy="12" r="1.3" /><circle cx="12" cy="12" r="1.3" /><circle cx="18" cy="12" r="1.3" /></>,
};

/** 경로 → DS2 하단 탭(방문자 무관 · 경로만). L6 — nav-config activeTabOf 의 별칭(한 함수). */
export const ds2TabOf = activeTabOf;

export interface NavV2Props {
  /** usePathname() — 활성 탭 판정에만 쓴다. */
  pathname: string;
  /** L6 — 라벨 한 벌 스위치(app_config nav/labels_v2 · 서버가 읽어 넘긴다). 생략 = 기본(켬). */
  labelsV2?: boolean;
  /** M6 — 하단 탭 재선정 스위치(app_config nav/tabs_v3 · 서버가 읽어 넘긴다). 생략 = 기본(끔 · 현행 6칸). */
  tabsV3?: boolean;
}

export default function NavV2({ pathname, labelsV2 = NAV_LABELS_V2_DEFAULT, tabsV3 = NAV_TABS_V3_DEFAULT }: NavV2Props) {
  const active = ds2TabOf(pathname, tabsV3);
  const top = activeTopOf(pathname);
  const DS2_TOP = topMenuOf(labelsV2).filter((m) => m.key !== 'tools');
  const tabs = navTabsOf(labelsV2, tabsV3);
  return (
    <>
      <header className="kd-mast" data-nav="v2">
        <div className="kd-mast__bar">
          <Link href="/" className="kd-mast__logo" aria-label="카더라 홈" prefetch={false} {...navEvAttrs('nav_click', 'logo')}>
            카더라
          </Link>
          <nav className="kd-mast__nav" aria-label="주 메뉴">
            <ul className="kd-mast__list">
              {DS2_TOP.map((m) => (
                <li key={m.key} className="kd-dd" data-nav-dd={m.key}>
                  <a href={m.href} className="kd-dd__top" aria-current={top === m.key ? 'page' : undefined} {...navEvAttrs('nav_click', m.key)}>
                    {m.label}
                  </a>
                </li>
              ))}
              {/* more-tidy — PC 「더보기」(광고 묶음 맨 앞 · /more 와 같은 표). 모바일은 하단 탭 「더보기」. */}
              <NavMoreMenu active={active === 'more'} />
            </ul>
          </nav>
          <div className="kd-mast__right">
            {/* nav-live — 종전 지역 칩 자리. 서버 HTML = 고정 폭 빈 자리(방문자 무관) · 숫자는 수화 뒤. */}
            <span className="kd-slot kd-slot--visitors" data-nav-slot="visitors">
              <NavVisitorCount />
            </span>
            <RegionMemorySync />
            <NavSearchSlot surface="mast" />
            <NavUserMenu pathname={pathname} />
          </div>
        </div>
      </header>

      {/* DS2 — 모바일 하단 탭 6(홈 · 분양 · 청약 · 재개발 · 블로그 · 더보기). 760px 이하에서만 보인다. */}
      <nav className="kd-tabbar" aria-label="하단 탭" data-nav="bottom" data-nav-labels={labelsV2 ? 'v2' : 'v1'} data-nav-tabs={tabsV3 ? 'v3' : 'v2'}>
        {tabs.map((t) => {
          const on = active === t.key;
          return (
            <a
              key={t.key}
              href={t.href}
              className="kd-tabbar__tab"
              aria-current={on ? 'page' : undefined}
              {...navEvAttrs('tab_click', t.key)}
            >
              <svg className="kd-tabbar__ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">{TAB_ICONS[t.key]}</svg>
              <span className="kd-tabbar__label" data-rd="label">{t.label}</span>
            </a>
          );
        })}
      </nav>

      <NavV2Effects />
      {/* CL §1-6 — 카톡 FAB(TalkFab)·SiteDock 은 렌더하지 않는다. 파일은 보존. */}
    </>
  );
}
